import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';

export default function ComputersPage() {
  const [computers, setComputers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', location: '', hardwareConfig: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [cmdCopied, setCmdCopied] = useState(false);
  const [copiedLicId, setCopiedLicId] = useState(null);
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [viewingComputer, setViewingComputer] = useState(null);
  const [computerKeys, setComputerKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [showQuickAssignModal, setShowQuickAssignModal] = useState(false);
  const [quickAssignComputer, setQuickAssignComputer] = useState(null);
  const [allLicenses, setAllLicenses] = useState([]);
  const [loadingQuickAssign, setLoadingQuickAssign] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [assigningQuick, setAssigningQuick] = useState(false);
  const [quickAssignError, setQuickAssignError] = useState('');
  const [unassigningLicId, setUnassigningLicId] = useState(null);

  // Search state
  const [searchText, setSearchText] = useState('');

  const fetchComputers = async () => {
    try {
      const res = await api.get('/computers');
      setComputers(res.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComputers(); }, []);

  const filteredComputers = useMemo(() => {
    if (!searchText.trim()) return computers;
    const q = searchText.toLowerCase();
    return computers.filter((comp) => {
      const nameMatch = comp.name.toLowerCase().includes(q);
      const locationMatch = comp.location && comp.location.toLowerCase().includes(q);
      const hardwareMatch = comp.hardwareConfig && comp.hardwareConfig.toLowerCase().includes(q);
      const licenseMatch = comp.licenses && comp.licenses.some((lic) =>
        lic.licenseType?.name?.toLowerCase().includes(q)
      );
      return nameMatch || locationMatch || hardwareMatch || licenseMatch;
    });
  }, [computers, searchText]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', location: '', hardwareConfig: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (comp) => {
    setEditing(comp);
    setForm({
      name: comp.name || '',
      location: comp.location || '',
      hardwareConfig: comp.hardwareConfig || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/computers/${editing.id}`, form);
      } else {
        await api.post('/computers', form);
      }
      setShowModal(false);
      fetchComputers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save computer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? Licenses assigned to this computer will be unassigned.')) return;
    try {
      await api.delete(`/computers/${id}`);
      fetchComputers();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleCopyLicenseKey = async (licId) => {
    try {
      const res = await api.get(`/licenses/${licId}`);
      const key = res.data.licenseKey;
      await navigator.clipboard.writeText(key);
      setCopiedLicId(licId);
      setTimeout(() => setCopiedLicId(null), 2000);
    } catch (err) {
      console.error('Failed to copy license key:', err);
    }
  };

  const openKeysModal = async (comp) => {
    setViewingComputer(comp);
    setShowKeysModal(true);
    setLoadingKeys(true);
    try {
      // Fetch full license details for each assigned license
      const keysPromises = (comp.licenses || []).map((lic) => api.get(`/licenses/${lic.id}`));
      const keysResponses = await Promise.all(keysPromises);
      setComputerKeys(keysResponses.map((res) => res.data));
    } catch (err) {
      console.error('Failed to fetch license keys:', err);
    } finally {
      setLoadingKeys(false);
    }
  };

  const copyKeyToClipboard = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleUnassign = async (lic) => {
    if (!viewingComputer) return;
    setUnassigningLicId(lic.id);
    try {
      const newComputerIds = (lic.computers || [])
        .filter((c) => c.id !== viewingComputer.id)
        .map((c) => c.id);
      await api.put(`/licenses/${lic.id}/assign`, { computerIds: newComputerIds });
      setComputerKeys((prev) => prev.filter((l) => l.id !== lic.id));
      fetchComputers();
    } catch (err) {
      console.error('Failed to unassign:', err);
    } finally {
      setUnassigningLicId(null);
    }
  };

  const openQuickAssignModal = async (comp) => {
    setQuickAssignComputer(comp);
    setSelectedTypeId('');
    setQuickAssignError('');
    setShowQuickAssignModal(true);
    setLoadingQuickAssign(true);
    try {
      const res = await api.get('/licenses');
      setAllLicenses(res.data);
    } catch (err) {
      setQuickAssignError('Failed to load licenses');
    } finally {
      setLoadingQuickAssign(false);
    }
  };

  const freeByType = useMemo(() => {
    if (!quickAssignComputer || !allLicenses.length) return {};
    return allLicenses.reduce((acc, lic) => {
      const alreadyAssigned = lic.computers?.some((c) => c.id === quickAssignComputer.id);
      const hasSlot = (lic.computers?.length || 0) < (lic.maxActivations || 1);
      if (!alreadyAssigned && hasSlot && lic.licenseType) {
        const tid = String(lic.licenseTypeId);
        if (!acc[tid]) acc[tid] = { name: lic.licenseType.name, licenses: [] };
        acc[tid].licenses.push(lic);
      }
      return acc;
    }, {});
  }, [allLicenses, quickAssignComputer]);

  const handleQuickAssign = async () => {
    if (!selectedTypeId || !quickAssignComputer) return;
    const candidates = freeByType[selectedTypeId]?.licenses || [];
    if (candidates.length === 0) return;
    const chosen = candidates[Math.floor(Math.random() * candidates.length)];
    const newComputerIds = [
      ...(chosen.computers || []).map((c) => c.id),
      quickAssignComputer.id,
    ];
    setAssigningQuick(true);
    setQuickAssignError('');
    try {
      await api.put(`/licenses/${chosen.id}/assign`, { computerIds: newComputerIds });
      setShowQuickAssignModal(false);
      fetchComputers();
    } catch (err) {
      setQuickAssignError(err.response?.data?.error || 'Failed to assign license');
    } finally {
      setAssigningQuick(false);
    }
  };

  const downloadScript = () => {
    const lines = [
      '# License Manager - Hardware Info Collector',
      '# Right-click this file -> "Run with PowerShell"',
      '# Or run in PowerShell: powershell -ExecutionPolicy Bypass -File .\\get-hardware-info.ps1',
      '',
      'Set-ExecutionPolicy -Scope Process -ExecutionPolicy Bypass -Force',
      '',
      '$cs   = Get-CimInstance Win32_ComputerSystem',
      '$cpu  = Get-CimInstance Win32_Processor',
      '$gpu  = Get-CimInstance Win32_VideoController',
      '$disk = Get-CimInstance Win32_DiskDrive',
      '$os   = Get-CimInstance Win32_OperatingSystem',
      '$ram  = Get-CimInstance Win32_PhysicalMemory',
      '',
      '$info = @()',
      '$info += "Computer: $($cs.Name)"',
      '$info += "Manufacturer: $($cs.Manufacturer) $($cs.Model)"',
      '$info += "OS: $($os.Caption) $($os.Version)"',
      '$info += "CPU: $($cpu.Name)"',
      '$info += "Cores: $($cpu.NumberOfCores) cores / $($cpu.NumberOfLogicalProcessors) threads"',
      '$info += "RAM: $([math]::Round($cs.TotalPhysicalMemory/1GB, 1)) GB ($($ram.Count)x $([math]::Round($ram[0].Capacity/1GB, 0))GB)"',
      '$gpuNames = ($gpu | ForEach-Object { $_.Name }) -join ", "',
      '$info += "GPU: $gpuNames"',
      '$diskInfo = ($disk | ForEach-Object { $_.Model + " - " + [math]::Round($_.Size/1GB, 0).ToString() + " GB" }) -join ", "',
      '$info += "Disk: $diskInfo"',
      '',
      '$result = $info -join [Environment]::NewLine',
      '',
      'Write-Host ""',
      'Write-Host "=== Hardware Configuration ===" -ForegroundColor Cyan',
      'Write-Host $result',
      'Write-Host "==============================" -ForegroundColor Cyan',
      'Write-Host ""',
      '',
      '$result | Set-Clipboard',
      'Write-Host "Copied to clipboard! Paste it into the Hardware Configuration field." -ForegroundColor Green',
      'Write-Host ""',
      'Write-Host "Press any key to close..." -ForegroundColor Gray',
      '$null = $Host.UI.RawUI.ReadKey("NoEcho,IncludeKeyDown")',
    ];
    const script = lines.join('\r\n');

    const blob = new Blob([script], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'get-hardware-info.ps1';
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyRunCommand = () => {
    navigator.clipboard.writeText('powershell -ExecutionPolicy Bypass -File .\\get-hardware-info.ps1');
    setCmdCopied(true);
    setTimeout(() => setCmdCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Computers</h1>
        <button onClick={openCreate} className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-medium rounded-lg transition-colors">
          + Add Computer
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 sm:flex-none sm:w-80">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by name, location, hardware, license..."
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
          {filteredComputers.length} / {computers.length}
        </span>
      </div>

      {filteredComputers.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-8 text-center text-gray-500 dark:text-gray-400">
          {computers.length === 0 ? 'No computers yet. Add your first computer to get started.' : 'No computers match your search.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredComputers.map((comp) => (
            <div key={comp.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-50 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{comp.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{comp.location || 'No location'}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {comp.licenses && comp.licenses.length > 0 && (
                    <button onClick={() => openKeysModal(comp)} className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-md transition-colors" title="View license keys">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </button>
                  )}
                  <button onClick={() => openQuickAssignModal(comp)} className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-md transition-colors" title="Assign license">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </button>
                  <button onClick={() => openEdit(comp)} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors" title="Edit">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button onClick={() => handleDelete(comp.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors" title="Delete">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {comp.hardwareConfig && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Hardware Config</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{comp.hardwareConfig}</p>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Assigned Licenses</p>
                {comp.licenses && comp.licenses.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {comp.licenses.map((lic) => (
                      <button
                        key={lic.id}
                        onClick={() => handleCopyLicenseKey(lic.id)}
                        title="Click to copy license key"
                        className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800/50 transition-colors cursor-pointer"
                      >
                        {copiedLicId === lic.id ? 'Copied!' : (lic.licenseType?.name || 'License')}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500">No licenses assigned</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between p-4 sm:p-6 pb-0 bg-white dark:bg-gray-800">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{editing ? 'Edit Computer' : 'Add Computer'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 -mr-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-4 sm:p-6 pt-4 space-y-4">
              {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm rounded-lg">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="e.g. PC-Office-01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="e.g. Building A, Room 101"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hardware Configuration</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={downloadScript}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 rounded-md transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      PS1 script
                    </button>
                    <button
                      type="button"
                      onClick={copyRunCommand}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {cmdCopied ? 'Copied!' : 'Copy command'}
                    </button>
                  </div>
                </div>
                <textarea
                  value={form.hardwareConfig}
                  onChange={(e) => setForm({ ...form, hardwareConfig: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="Run the PS1 script on the target PC, then paste the result here"
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:bg-indigo-400 text-white text-sm font-medium rounded-lg transition-colors">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Assign Modal */}
      {showQuickAssignModal && quickAssignComputer && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowQuickAssignModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md">
            <div className="flex items-center justify-between p-4 sm:p-6 pb-0">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Assign License</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{quickAssignComputer.name}</p>
              </div>
              <button onClick={() => setShowQuickAssignModal(false)} className="p-2 -mr-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 pt-4 space-y-4">
              {quickAssignError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm rounded-lg">
                  {quickAssignError}
                </div>
              )}
              {loadingQuickAssign ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : Object.keys(freeByType).length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                  No available license keys to assign.
                </p>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Software
                  </label>
                  <select
                    value={selectedTypeId}
                    onChange={(e) => setSelectedTypeId(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  >
                    <option value="">Select software...</option>
                    {Object.entries(freeByType)
                      .sort((a, b) => a[1].name.localeCompare(b[1].name))
                      .map(([tid, { name, licenses }]) => (
                        <option key={tid} value={tid}>
                          {name} ({licenses.length} available)
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    A random available key will be assigned to this computer.
                  </p>
                </div>
              )}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowQuickAssignModal(false)}
                  className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleQuickAssign}
                  disabled={!selectedTypeId || assigningQuick}
                  className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:bg-indigo-300 dark:disabled:bg-indigo-800 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {assigningQuick ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Keys Modal */}
      {showKeysModal && viewingComputer && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowKeysModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between p-4 sm:p-6 pb-0 bg-white dark:bg-gray-800">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">License Keys</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{viewingComputer.name}</p>
              </div>
              <button onClick={() => setShowKeysModal(false)} className="p-2 -mr-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 pt-4">
              {loadingKeys ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : computerKeys.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">No licenses assigned</p>
              ) : (
                <div className="space-y-3">
                  {computerKeys.map((lic) => (
                    <div key={lic.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">
                          {lic.licenseType?.name || 'License'}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              copyKeyToClipboard(lic.licenseKey);
                              setCopiedLicId(lic.id);
                              setTimeout(() => setCopiedLicId(null), 2000);
                            }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                          >
                            {copiedLicId === lic.id ? (
                              <>
                                <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Copied!
                              </>
                            ) : (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleUnassign(lic)}
                            disabled={unassigningLicId === lic.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 rounded-md transition-colors"
                          >
                            {unassigningLicId === lic.id ? (
                              <>
                                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Removing...
                              </>
                            ) : (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Unassign
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <code className="block text-sm font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded px-3 py-2 border border-gray-200 dark:border-gray-600 break-all select-all">
                        {lic.licenseKey}
                      </code>
                      {lic.comment && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{lic.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
